import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/react-hook-form'
import { useTranslation } from 'react-i18next'

import { createAccount } from '../api/create-account'
import { JointAccountTypes } from '../../../shared/_types'

import Checkbox from '../../../../components/Checkbox'
import CountryInput from '../../../../components/CountryInput'
import ErrorAlert from '../../../../components/ErrorAlert'
import FormFooter from '../../../shared/FormFooter'
import NumberInput from '../../../../components/NumberInput'
import Radio from '../../../../components/Radio'
import StateDropdown from '../../../../components/StateDropdown'
import TextInput from '../../../../components/TextInput'

const OPTION_STATES = ['AZ', 'CA', 'ID', 'LA', 'NV', 'NM', 'TX', 'WI', 'WA']

const NewJointAccount = () => {
  const [use_primary_investor_contact, setUse_primary_investor_contact] =
    useState(false)
  const nav = useNavigate()
  const queryClient = useQueryClient()

  const {
    control,
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<JointAccountTypes>({
    defaultValues: {
      type: 'joint_ownership',
      subtype: 'community',
      joint_first_name: '',
      joint_last_name: '',
      joint_phone_number: '',
      use_primary_investor_contact: false,
      address: {
        street: '',
        street2: '',
        city: '',
        zip_code: '',
        state: 'AL',
        country: 'US',
      },
    },
  })
  const { t } = useTranslation()
  const watchAddress = watch('address')
  const watchState = watch('address.state')

  const createAccountMutation = useMutation({
    mutationFn: (data: JointAccountTypes) => {
      const payload = { ...data }
      if (use_primary_investor_contact) {
        delete payload.address
        delete payload.joint_phone_number
      }
      return createAccount(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ownership-types'],
      })
      nav('/dashboard/ownership-types')
    },
    onError: (error: any) => {
      console.error(error.message)
    },
  })

  const onSubmit = handleSubmit(async (data) => {
    createAccountMutation.mutateAsync(data)
  })

  return (
    <div className="p-6 xl:p-0">
      <div className="w-full max-w-2xl mx-auto">
        <h1 className="mb-2 text-2xl font-bold text-content-black">
          {t('common:ownershipType')}
        </h1>
        <p className="mb-6 italic text-content-black">{t('common:joint')}</p>
        <p className="mb-6 text-16 text-content-black">
          Provide the details for this joint ownership account.
        </p>
        {Object.keys(errors).length > 0 && <ErrorAlert />}
        <form onSubmit={onSubmit} className="xl:">
          <div className="mb-5">
            <div>
              <p className="mb-6 text-[19px] font-medium">
                {t('common:joint')}
              </p>
              <div className="mb-6 flex gap-3">
                <Controller
                  name="joint_first_name"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={t('common:firstName')}
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="joint_last_name"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={t('common:lastName')}
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
              </div>
              <div className="mb-6">
                <Controller
                  name="joint_is_spouse"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      {...field}
                      checked={field.value === true}
                      errorMessage={fieldState.error}
                      label={t('account:spouse')}
                    />
                  )}
                />
              </div>
              {OPTION_STATES.includes(watchState || '') && (
                <div className="mb-6">
                  <p className="mb-6 text-16 text-content-black">
                    {t('account:jointType')}
                  </p>
                  <Controller
                    name="subtype"
                    control={control}
                    rules={{ required: 'is required' }}
                    render={({ field, fieldState }) => (
                      <>
                        <div
                          className={`flex flex-col gap-px ${
                            fieldState.error && 'border border-alert'
                          }`}
                        >
                          <Radio
                            {...field}
                            value="community"
                            label={t('account:communityProperty')}
                            checked={field.value === 'community'}
                          />
                          <Radio
                            {...field}
                            value="survivorship"
                            label={t('account:survivorship')}
                            checked={field.value === 'survivorship'}
                          />
                        </div>
                        {fieldState.error && (
                          <span className="mb-1 text-xs text-alert">
                            {fieldState.error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
              )}
              <h2 className="mb-6 text-2xl font-bold text-content-black">
                {t('account:jointInfoContact')}
              </h2>
              <div className="mb-6">
                <Controller
                  name="use_primary_investor_contact"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      {...field}
                      onChange={() => {
                        field.onChange(!field.value)
                        setUse_primary_investor_contact(
                          !use_primary_investor_contact
                        )
                      }}
                      checked={field.value === true}
                      label={t('account:sameAddress')}
                      errorMessage={fieldState.error}
                    />
                  )}
                />
              </div>
              {!use_primary_investor_contact && (
                <div>
                  <div className="mb-6">
                    <span
                      className={`mb-2 block text-base font-normal ${
                        errors?.joint_phone_number?.message
                          ? 'text-alert'
                          : 'text-content-black'
                      }`}
                    >
                      {t('common:phoneNumber')}
                    </span>
                    <PhoneInput
                      name="joint_phone_number"
                      className={`phone-input-wrapper mb-2 h-[42px] w-full rounded border ${
                        errors?.joint_phone_number
                          ? 'border-alert'
                          : 'border-input'
                      } px-3 py-1.5 text-16 focus-within:outline-none focus-within:ring-1 focus-within:border-primary`}
                      defaultCountry="US"
                      countryOptionsOrder={['US']}
                      control={control}
                      initialValueFormat="national"
                      limitMaxLength={true}
                      rules={{
                        required: 'is required',
                        validate: {
                          isValidPhoneNumber: (value: string) =>
                            isValidPhoneNumber(value) ||
                            'Please enter a valid phone number.',
                        },
                      }}
                    />
                    {errors?.joint_phone_number?.message && (
                      <p className="mb-1 text-xs text-alert">
                        {errors?.joint_phone_number?.message}
                      </p>
                    )}
                  </div>
                  <div className="mb-6">
                    <CountryInput
                      register={register('address.country')}
                      errorMessage={errors?.address?.country?.message}
                    />
                  </div>
                  <div className="mb-2">
                    <Controller
                      name="address.street"
                      control={control}
                      rules={{ required: 'is required' }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label={t('common:address')}
                          errorMessage={fieldState.error}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-6">
                    <Controller
                      name="address.street2"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextInput
                          placeholder={t('common:address2')}
                          errorMessage={fieldState.error}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-6">
                    <Controller
                      name="address.city"
                      control={control}
                      rules={{ required: 'is required' }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label={t('common:city')}
                          errorMessage={fieldState.error}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-6 flex gap-3">
                    <div className="w-1/2">
                      <Controller
                        name="address.state"
                        control={control}
                        rules={{
                          required:
                            watchAddress?.country === 'US'
                              ? 'is required'
                              : false,
                        }}
                        render={({ field, fieldState }) =>
                          watchAddress?.country === 'US' ? (
                            <StateDropdown
                              errorMessage={fieldState?.error?.message}
                              label={t('common:state')}
                              {...field}
                            />
                          ) : (
                            <TextInput
                              label={
                                watchAddress?.country === 'US'
                                  ? t('common:state')
                                  : t('common:stateProvinceRegion')
                              }
                              errorMessage={fieldState?.error?.message}
                              {...field}
                            />
                          )
                        }
                      />
                    </div>
                    <div className="w-1/2">
                      <Controller
                        name="address.zip_code"
                        control={control}
                        rules={{ required: 'is required' }}
                        render={({ field, fieldState }) => (
                          <NumberInput
                            label={
                              watchAddress?.country === 'US'
                                ? t('common:zipCode')
                                : t('common:postalCode')
                            }
                            errorMessage={fieldState.error}
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <FormFooter
            backLocation="/dashboard/ownership-types/new"
            submitFunc={onSubmit}
            disabled={createAccountMutation.isLoading}
          />
        </form>
      </div>
    </div>
  )
}

export default NewJointAccount
